import { useAnnotationStore } from "@frontend/stores/annotation-store"
import { useHistoryStore } from "@frontend/stores/history-store"
import type { LabelRasterManifestEntry, MaskNiftiEntry } from "@shared/lib/history-types"
import { useMutation } from "@tanstack/react-query"
import { useCallback, useEffect, useRef } from "react"

type UploadReason = "save" | "submit"

function toBlobPart(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

interface UseSubmissionHistorySyncParams {
  submissionId: string | null
  caseId: string | undefined
  isBootstrappingSession: boolean
  getAuthHeaders: () => Promise<Record<string, string> | undefined>
  intervalMs?: number
  isReadOnly?: boolean
  /** Optional callback to generate NIfTI files for mask overlay volumes at save time. */
  generateMaskNiftis?: () => Promise<MaskNiftiEntry[]>
  /**
   * Optional callback producing encoded label-raster chunk-diff manifests
   * (one per label_raster annotation) at save time. Synchronous: the manifest
   * is derived purely from the snapshot's sparse runs.
   */
  generateLabelRasterManifests?: () => LabelRasterManifestEntry[]
}

export function useSubmissionHistorySync({
  submissionId,
  caseId,
  isBootstrappingSession,
  getAuthHeaders,
  intervalMs = 15_000,
  isReadOnly = false,
  generateMaskNiftis,
  generateLabelRasterManifests
}: UseSubmissionHistorySyncParams) {
  const isHistoryDirty = useHistoryStore((s) => s.isDirty)
  const isUploadingHistory = useHistoryStore((s) => s.isUploading)
  const checkpointCount = useHistoryStore((s) => s.checkpointCount())

  const historyUploadMutation = useMutation({
    mutationFn: async (params: { sid: string; payload: Uint8Array }) => {
      const headers = await getAuthHeaders()
      const checkpointHeader = String(useHistoryStore.getState().checkpointCount())
      const response = await fetch(`/api/submissions/${params.sid}/history/file`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/cbor",
          "X-Checkpoint-Count": checkpointHeader,
          ...(headers ?? {})
        },
        body: new Blob([toBlobPart(params.payload)], {
          type: "application/cbor"
        })
      })

      if (!response.ok) {
        throw new Error(`History upload failed (${response.status})`)
      }
    }
  })

  const uploadHistoryPayload = useCallback(
    async (sid: string, payload: Uint8Array) => {
      const state = useHistoryStore.getState()
      state.markUploadStart()
      try {
        await historyUploadMutation.mutateAsync({ sid, payload })
        useHistoryStore.getState().markUploadSuccess()
        return true
      } catch (error) {
        useHistoryStore.getState().markUploadError(error instanceof Error ? error.message : "History upload failed")
        return false
      }
    },
    [historyUploadMutation]
  )

  const generateMaskNiftisRef = useRef(generateMaskNiftis)
  useEffect(() => {
    generateMaskNiftisRef.current = generateMaskNiftis
  }, [generateMaskNiftis])
  const generateLabelRasterManifestsRef = useRef(generateLabelRasterManifests)
  useEffect(() => {
    generateLabelRasterManifestsRef.current = generateLabelRasterManifests
  }, [generateLabelRasterManifests])
  /** `${id}:${editedAt}` keys of the manifests last written to the session. */
  const lastLabelRasterKeysRef = useRef<string>("")

  const uploadHistoryNow = useCallback(
    async (reason: UploadReason) => {
      const sid = submissionId
      if (!(sid && caseId)) return

      const state = useHistoryStore.getState()
      const snapshot = useAnnotationStore.getState().getSnapshot()
      state.captureCheckpoint(snapshot, reason)

      // Generate NIfTI files for mask volumes before serializing CBOR.
      // Autosaves can reuse stored NIfTIs while the mask set is unchanged, but
      // submissions always regenerate so edits to existing masks are included.
      if (generateMaskNiftisRef.current) {
        const existingNiftis = useHistoryStore.getState().session?.maskNiftis ?? []
        const existingIds = new Set(existingNiftis.map((entry) => entry.annotationId))
        const currentIds = new Set(snapshot.masks.map((mask) => mask.id))
        const maskSetChanged =
          currentIds.size !== existingIds.size || [...currentIds].some((id) => !existingIds.has(id))
        const needsRegeneration = reason === "submit" || maskSetChanged

        if (snapshot.masks.length === 0) {
          if (existingNiftis.length > 0) {
            useHistoryStore.getState().setMaskNiftis([])
          }
        } else if (needsRegeneration) {
          let generatedNiftis: MaskNiftiEntry[] = []
          try {
            generatedNiftis = await generateMaskNiftisRef.current()
          } catch {
            // NIfTI generation is best-effort; RLE remains authoritative.
          }

          const freshById = new Map<string, MaskNiftiEntry>()
          for (const entry of generatedNiftis) {
            if (currentIds.has(entry.annotationId)) {
              freshById.set(entry.annotationId, entry)
            }
          }
          const freshNiftis = [...freshById.values()]

          // On submit, replace stale derivatives even when generation failed or
          // was partial. Missing NIfTIs can be rebuilt from authoritative RLE.
          if (reason === "submit" || freshNiftis.length > 0) {
            useHistoryStore.getState().setMaskNiftis(freshNiftis)
          }
        }
      }

      // Label-raster manifests are cheap to derive from the snapshot's sparse
      // runs; regenerate whenever the edit set or any editedAt changed.
      if (generateLabelRasterManifestsRef.current) {
        const rasters = snapshot.labelRasters ?? []
        const keys = rasters.map((r) => `${r.id}:${r.editedAt}`).join("|")
        const existing = useHistoryStore.getState().session?.labelRasterManifests ?? []
        if (rasters.length === 0) {
          if (existing.length > 0) {
            useHistoryStore.getState().setLabelRasterManifests([])
          }
          lastLabelRasterKeysRef.current = ""
        } else if (reason === "submit" || keys !== lastLabelRasterKeysRef.current || existing.length === 0) {
          try {
            const entries = generateLabelRasterManifestsRef.current()
            useHistoryStore.getState().setLabelRasterManifests(entries)
            lastLabelRasterKeysRef.current = keys
          } catch {
            // Manifest generation is best-effort; snapshot runs remain authoritative.
          }
        }
      }

      const payload = useHistoryStore.getState().toCbor()
      if (!payload) return

      const ok = await uploadHistoryPayload(sid, payload)
      if (!ok && reason === "submit") {
        throw new Error("History upload failed")
      }
    },
    [caseId, submissionId, uploadHistoryPayload]
  )

  const uploadHistoryNowRef = useRef(uploadHistoryNow)
  useEffect(() => {
    uploadHistoryNowRef.current = uploadHistoryNow
  }, [uploadHistoryNow])

  useEffect(() => {
    if (!submissionId || isBootstrappingSession || isReadOnly) return

    const interval = setInterval(() => {
      const state = useHistoryStore.getState()
      if (!state.isDirty || state.isUploading) return
      void uploadHistoryNowRef.current("save")
    }, intervalMs)

    return () => clearInterval(interval)
  }, [intervalMs, isBootstrappingSession, isReadOnly, submissionId])

  return {
    uploadHistoryNow,
    isHistoryDirty,
    isUploadingHistory,
    checkpointCount
  }
}
